import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApplicationForm } from '../hooks/useApplicationForm';
import { getApplicationFormDraft, clearApplicationFormDraft } from '../utils/storage';
import { useSessionClass } from '../contexts/SessionClassContext';
import { useQuery, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { AdmissionFromModal, AdmissionFromData } from '../components/Modals/AdmissionFromModal';
import ApplicationFormTemplate from '../components/templates/ApplicationFormTemplate';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '../theme';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

const AdmissionApplicationFromManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedClass, selectedSession } = useSessionClass();
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentForm, setCurrentForm] = useState<any>(undefined);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [viewAdmissionModalOpen, setViewAdmissionModalOpen] = useState(false);
  const [viewAdmissionData, setViewAdmissionData] = useState<any>(null);
  const [foundApplication, setFoundApplication] = useState<any>(null);
  const [mobileNo, setMobileNo] = useState<string>("");
  const [dob, setDob] = useState<Date | null>(null);
  const [searchError, setSearchError] = useState<string>("");
  const [restoreDraftDialogOpen, setRestoreDraftDialogOpen] = useState(false);
  const [draftData, setDraftData] = useState<any | null>(null);

  // Get school ID from user
  const schoolId = user?.schoolId;

  // Format date directly to ISO format (yyyy-MM-dd) to avoid ambiguity
  const formatDateToISO = (date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // State to control when to actually search (only when user clicks search button)
  const [shouldSearch, setShouldSearch] = useState(false);
  const [searchMobileNo, setSearchMobileNo] = useState<string>("");
  const [searchDob, setSearchDob] = useState<string>("");

  // Use the hook with mobileNo and dob parameters - only search when shouldSearch is true
  const { applicationFormsByMobileNoAndDOBQuery, createApplicationFormMutation, updateApplicationFormMutation } = useApplicationForm(
    false,
    schoolId || undefined,
    shouldSearch ? searchMobileNo : undefined,
    shouldSearch ? searchDob : undefined
  );

  // Fetch school data
  const schoolDataQuery = useQuery({
    queryKey: ["schoolById", schoolId],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/schools/${schoolId}`)
        .then((res) => res.data),
    enabled: !!schoolId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Use cached data instead of refetching on every mount
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
  const schoolData = schoolDataQuery.data;
  const schoolLoading = schoolDataQuery.isLoading;

  // Handle search
  const handleSearch = useCallback(() => {
    if (!mobileNo.trim()) {
      setSearchError(t("please_enter_mobile_number") || "Please enter mobile number");
      return;
    }
    if (!dob) {
      setSearchError(t("please_select_date_of_birth") || "Please select date of birth");
      return;
    }
    setSearchError("");
    // Set search parameters and trigger search
    // Convert Date directly to ISO format (yyyy-MM-dd) to avoid parsing ambiguity
    setSearchMobileNo(mobileNo.trim());
    setSearchDob(formatDateToISO(dob));
    setShouldSearch(true);
  }, [mobileNo, dob, t]);

  // Handle successful search result
  useEffect(() => {
    if (applicationFormsByMobileNoAndDOBQuery.data && shouldSearch) {
      setFoundApplication(applicationFormsByMobileNoAndDOBQuery.data);
      setSearchDialogOpen(false);
      // Reset search fields and state
      setMobileNo("");
      setDob(null);
      setShouldSearch(false);
      setSearchMobileNo("");
      setSearchDob("");
      setSearchError("");
    }
  }, [applicationFormsByMobileNoAndDOBQuery.data, shouldSearch]);

  // Handle search error
  useEffect(() => {
    if (applicationFormsByMobileNoAndDOBQuery.error && shouldSearch) {
      setSearchError(t("application_form_not_found") || "Application form not found");
      setShouldSearch(false);
    }
  }, [applicationFormsByMobileNoAndDOBQuery.error, shouldSearch, t]);

  const handleSubmitApplication = async (formData: AdmissionFromData) => {
    // Validate required data
    if (!selectedSession) {
      alert(t('please_select_session_first'));
      return;
    }
    if (!selectedClass) {
      alert(t('please_select_class_first'));
      return;
    }
    if (!schoolId) {
      alert(t('school_id_not_available'));
      return;
    }

    // Add required IDs to formData (same as ApplicationFormManagement)
    const submitData: AdmissionFromData = {
      ...formData,
      sessionId: selectedSession.id!,
      classId: selectedClass.id!,
      sessionName: selectedSession.name,
      schoolId: schoolId, // Already validated above
    };


    try {
      if (isEdit && currentForm?.id) {
        await updateApplicationFormMutation.mutateAsync({
          id: currentForm.id,
          data: submitData,
        });
        // Update found application if it's the same one being edited
        if (foundApplication && foundApplication.id === currentForm.id) {
          setFoundApplication({ ...foundApplication, ...submitData });
        }
      } else {
        await createApplicationFormMutation.mutateAsync(submitData);
        // Clear draft data after successful submission
        clearApplicationFormDraft();
      }
      setModalOpen(false);
      setCurrentForm(undefined);
      setIsEdit(false);
    } catch (error) {
      // Don't clear draft on error - allow user to retry with saved data
    }
  };

  const handleOpenAddModal = useCallback(() => {
    setIsEdit(false);

    // Check for draft data
    const savedDraft = getApplicationFormDraft();
    if (savedDraft) {
      setDraftData(savedDraft);
      setRestoreDraftDialogOpen(true);
    } else {
      setCurrentForm(undefined);
      setModalOpen(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setCurrentForm(undefined);
    setIsEdit(false);
  }, []);

  const formatGender = useCallback((gender: string | null | undefined): string => {
    if (!gender) return "";
    const g = gender.toLowerCase();
    if (g === "m" || g === "male") return t("male") || "Male";
    if (g === "f" || g === "female") return t("female") || "Female";
    return gender;
  }, [t]);

  const formatStatus = useCallback((status: string | null | undefined): string => {
    if (!status) return t("pending") || "Pending";
    const s = status.toLowerCase();
    if (s === "pending") return t("pending") || "Pending";
    if (s === "approved") return t("approved") || "Approved";
    if (s === "rejected") return t("rejected") || "Rejected";
    return status;
  }, [t]);

  const getStatusColor = useCallback((status: string | null | undefined) => {
    if (!status) return { bg: "#fff3e0", color: "#f57c00", border: "#ff9800" };
    const s = status.toLowerCase();
    if (s === "pending") return { bg: "#fff3e0", color: "#f57c00", border: "#ff9800" };
    if (s === "approved") return { bg: "#e8f5e9", color: "#2e7d32", border: "#4caf50" };
    if (s === "rejected") return { bg: "#ffebee", color: "#c62828", border: "#f44336" };
    return { bg: "#fff3e0", color: "#f57c00", border: "#ff9800" };
  }, []);

  const handleEditApplication = useCallback((application: any) => {
    setIsEdit(true);
    setCurrentForm(application);
    setModalOpen(true);
  }, []);

  const handleViewApplication = useCallback((application: any) => {
    setViewAdmissionData(application);
    setViewAdmissionModalOpen(true);
  }, []);

  // Show loading state while data is being fetched
  if (schoolLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          backgroundColor: "#f8f9fb",
          borderRadius: 3,
          border: "1px solid #e0e7ff",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              color: "#0168EA",
              fontWeight: 700,
            }}
          >
            {t("application_form")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={() => {
                setSearchDialogOpen(true);
                // Reset search state when opening dialog
                setShouldSearch(false);
                setSearchMobileNo("");
                setSearchDob("");
                setSearchError("");
              }}
              variant="outlined"
              size="large"
              startIcon={<SearchIcon />}
              sx={{
                borderColor: "#0168EA",
                color: "#0168EA",
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#0056d3",
                  backgroundColor: "rgba(1, 104, 234, 0.04)",
                },
              }}
            >
              {t("search_application") || "Search Application"}
            </Button>
            <Button
              onClick={handleOpenAddModal}
              variant="contained"
              size="large"
              disabled={createApplicationFormMutation.isLoading || updateApplicationFormMutation.isLoading}
              sx={{
                backgroundColor: "#0168EA",
                fontSize: "1rem",
                fontWeight: 600,
                boxShadow: "0 6px 20px rgba(1, 104, 234, 0.3)",
                "&:hover": {
                  backgroundColor: "#0056d3",
                  boxShadow: "0 8px 24px rgba(1, 104, 234, 0.4)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {createApplicationFormMutation.isLoading || updateApplicationFormMutation.isLoading
                ? t("creating_application")
                : t("start_new_application")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Found Application Table */}
      {foundApplication && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mt: 3,
            backgroundColor: "#f8f9fb",
            borderRadius: 3,
            border: "1px solid #e0e7ff",
          }}
        >
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="h5"
              sx={{
                color: "#0168EA",
                fontWeight: 700,
              }}
            >
              {t("found_application") || "Found Application"}
            </Typography>
            <Button
              onClick={() => setFoundApplication(null)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: "#0168EA",
                color: "#0168EA",
              }}
            >
              {t("clear") || "Clear"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("s_no") || "S.No."}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("application_no") || "Application No."}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("student_name") || "Student Name"}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("father_name") || "Father Name"}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("gender") || "Gender"}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("mobile_number") || "Mobile Number"}
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("status") || "Status"}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("actions") || "Actions"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell align="center">1</TableCell>
                  <TableCell>{foundApplication.applicationNo || "-"}</TableCell>
                  <TableCell>{foundApplication.studentName || "-"}</TableCell>
                  <TableCell>{foundApplication.fatherName || "-"}</TableCell>
                  <TableCell>{formatGender(foundApplication.gender)}</TableCell>
                  <TableCell>{foundApplication.mobileNo || "-"}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        backgroundColor: getStatusColor(foundApplication.isRegistered).bg,
                        color: getStatusColor(foundApplication.isRegistered).color,
                        border: `1px solid ${getStatusColor(foundApplication.isRegistered).border}`,
                      }}
                    >
                      {formatStatus(foundApplication.isRegistered)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                      <IconButton
                        onClick={() => handleViewApplication(foundApplication)}
                        size="small"
                        sx={{
                          color: "#0168EA",
                          "&:hover": {
                            backgroundColor: "rgba(1, 104, 234, 0.1)",
                          },
                        }}
                        title={t("view") || "View"}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {foundApplication.isRegistered?.toLowerCase() !== "approved" && (
                        <IconButton
                          onClick={() => handleEditApplication(foundApplication)}
                          size="small"
                          sx={{
                            color: "#0168EA",
                            "&:hover": {
                              backgroundColor: "rgba(1, 104, 234, 0.1)",
                            },
                          }}
                          title={t("edit") || "Edit"}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => {
          setSearchDialogOpen(false);
          setMobileNo("");
          setDob(null);
          setSearchError("");
          setShouldSearch(false);
          setSearchMobileNo("");
          setSearchDob("");
          setFoundApplication(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{t("search_application_form") || "Search Application Form"}</Typography>
          <IconButton
            onClick={() => {
              setSearchDialogOpen(false);
              setMobileNo("");
              setDob(null);
              setSearchError("");
              setShouldSearch(false);
              setSearchMobileNo("");
              setSearchDob("");
              setFoundApplication(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("mobile_number") || "Mobile Number"}
                  value={mobileNo}
                  onChange={(e) => {
                    setMobileNo(e.target.value);
                    setSearchError("");
                  }}
                  placeholder={t("enter_mobile_number") || "Enter mobile number"}
                  error={!!searchError && !mobileNo.trim()}
                  helperText={searchError && !mobileNo.trim() ? searchError : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("date_of_birth") || "Date of Birth"}
                    value={dob}
                    onChange={(newValue) => {
                      setDob(newValue);
                      setSearchError("");
                    }}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!searchError && !dob,
                        helperText: searchError && !dob ? searchError : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              {searchError && mobileNo && dob && (
                <Grid item xs={12}>
                  <Alert severity="error">{searchError}</Alert>
                </Grid>
              )}
              {shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSearchDialogOpen(false);
              setMobileNo("");
              setDob(null);
              setSearchError("");
              setShouldSearch(false);
              setSearchMobileNo("");
              setSearchDob("");
              setFoundApplication(null);
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSearch}
            variant="contained"
            disabled={!mobileNo.trim() || !dob || (shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading)}
            sx={{
              backgroundColor: "#0168EA",
              "&:hover": {
                backgroundColor: "#0056d3",
              },
            }}
          >
            {shouldSearch && applicationFormsByMobileNoAndDOBQuery.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("search") || "Search"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Form User Modal */}
      <AdmissionFromModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitApplication}
        initialData={currentForm}
        isEdit={isEdit}
        schoolData={{
          nameofSchool: schoolData?.nameofSchool || '',
          blockName: schoolData?.blockName || '',
          district: schoolData?.district || '',
          diseCode: schoolData?.diseCode || '',
          state: schoolData?.state || '',
          pinNo: schoolData?.pinNo || '',
          schoolCode: schoolData?.schoolCode || '',
          schoolLogo: schoolData?.schoolLogo || '',
          medium: schoolData?.medium || [],
        }}
        selectedClass={selectedClass}
      />

      {/* View Admission Modal */}
      {viewAdmissionData && (
        <Dialog
          open={viewAdmissionModalOpen}
          onClose={() => setViewAdmissionModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">{t("view_application_form")}</Typography>
            <IconButton onClick={() => setViewAdmissionModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <QueryClientProvider client={new QueryClient()}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <ApplicationFormTemplate
                  data={viewAdmissionData}
                  schoolData={schoolData}
                />
              </ThemeProvider>
            </QueryClientProvider>
          </DialogContent>
        </Dialog>
      )}

      {/* Restore Draft Dialog */}
      <Dialog
        open={restoreDraftDialogOpen}
        onClose={() => {
          // Default to fresh start if dialog is closed without choice
          clearApplicationFormDraft();
          setDraftData(null);
          setRestoreDraftDialogOpen(false);
          setCurrentForm(undefined);
          setModalOpen(true);
        }}
        aria-labelledby="restore-draft-dialog-title"
        aria-describedby="restore-draft-dialog-description"
      >
        <DialogTitle id="restore-draft-dialog-title">
          {t('restore_previous_data') || 'Restore Previous Data?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-draft-dialog-description">
            {t('restore_draft_message_application') || 'We found previously filled application data. Would you like to restore it?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              clearApplicationFormDraft();
              setDraftData(null);
              setRestoreDraftDialogOpen(false);
              setCurrentForm(undefined);
              setModalOpen(true);
            }}
            variant="outlined"
            sx={{ borderColor: "#666", color: "#666" }}
          >
            {t('no_start_fresh') || 'No, Start Fresh'}
          </Button>
          <Button
            onClick={() => {
              // Use draft data as initial data
              setCurrentForm(draftData);
              setRestoreDraftDialogOpen(false);
              setModalOpen(true);
              setIsEdit(false); // Still new creation, just pre-filled
            }}
            variant="contained"
            autoFocus
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t('yes_restore') || 'Yes, Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default AdmissionApplicationFromManagement;

